import metadataAliases from './metadata-aliases'
import { isV5, isV6 } from 'scoreboard/components/version'

/**
 * Metadata for our domain objects that doesn't have dependencies on files
 * specific to a module, so that we can avoid circular references.  The metadata
 * is keyed by both section (e.g. 'scorecards') and domainType
 * (e.g. 'scorecard-item')
 *
 * addLabelCode - the i18n message code to use for the "Add" button. Must be
 * provided by subclass. e.g. 'scorecards.newItem'
 *
 * noTypeText - complete noType text code, no substitution.
 * e.g. "There are no datasets."
 *
 * noTypeCode - the message substitution for 'common.missing.domainObject.1',
 * e.g. "This organization has no Scorecards."
 *
 * createTypeCode - the message substitution for 'common.create2'
 * e.g. "Create Scorecard"
 *
 * sectionCode - the message code for the section name, e.g. "Scorecards"
 *
 * sectionIcon - the suffix of the icon for the section, e.g. "scorecard" for
 * the scorecards section
 *
 * section - the name of the section (e.g. 'scorecards')
 *
 * domainType - the domainType string (e.g. 'scorecard-item')
 */

var metadata = {

	projects: {
		section: 'projects',
		domainType: 'project',
		// TODO: Remove When V5 is no longer supported (only remove V5 value)
		addLabelCode: isV5() ? 'common.project.project' : 'common.project.addProject',
		noTypeText: 'common.project.missing.domainObject',
		createTypeCode: 'common.project.project',
		sectionCode: 'common.project.project',
		singularCode: 'common.project.project',
		pluralCode: 'common.project.projects'
	},

	organizations: {
		section: 'organizations',
		domainType: 'organization',
		addLabelCode: 'common.organization.organization',
		createTypeCode: 'common.organization.organization',
		sectionCode: 'common.organization.organizations',
		sectionIcon: 'circle',
		singularCode: 'common.organization.organization',
		pluralCode: 'common.organization.organizations'
	},

	scorecards: {
		section: 'scorecards',
		domainType: 'scorecard-item',
		addLabelCode: isV6() ? 'scorecards.scorecardNode' : 'scorecards.newItem',
		noTypeCode: 'scorecards.scorecard',
		// TODO: Remove When V5 is no longer supported (only remove V5 value)
		noTypeHeader:  isV6() ? 'scorecards.noTypeHeader' : undefined,
		// TODO: Remove When V5 is no longer supported (only remove V5 value)
		noTypeText:  isV6() ? 'scorecards.noTypeText' : undefined,
		// TODO: Remove When V5 is no longer supported (only remove V5 value)
		noTypeLearnMore:  isV6() ? 'https://www.spiderstrategies.com/balanced-scorecard-software/' : undefined,
		createTypeCode: 'scorecards.scorecard',
		sectionCode: 'scorecards.scorecards',
		sectionIcon: 'scorecard',
		sectionIconNew: 'scorecard-new',
		singularCode: 'scorecards.scorecard',
		pluralCode: 'scorecards.scorecards',
		itemCode: 'scorecards.scorecardNode',
		requiresOrg: true
	},

	strategyMaps: {
		section: 'strategyMaps',
		domainType: 'strategy-map',
		addLabelCode: isV6() ? 'strategyMaps.strategyMap' : 'strategyMaps.strategyMap.new',
		noTypeCode: 'strategyMaps.strategyMaps',
		createTypeCode: 'strategyMaps.strategyMap',
		sectionCode: 'strategyMaps.strategyMaps',
		sectionIcon: 'strategy-map',
		singularCode: 'strategyMaps.strategyMap',
		pluralCode: 'strategyMaps.strategyMaps',
		requiresOrg: true
	},

	charts: {
		section: 'charts',
		domainType: 'chart',
		addLabelCode:  isV6() ? 'charts.chart' : 'charts.chart.add',
		noTypeCode: 'charts.charts',
		createTypeCode: 'charts.chart',
		sectionCode: 'charts.charts',
		sectionIcon: 'chart',
		singularCode: 'charts.chart',
		pluralCode: 'charts.charts',
		requiresOrg: true
	},

	dashboards: {
		section: 'dashboards',
		domainType: 'dashboard',
		addLabelCode: isV6() ? 'dashboards.dashboard' : 'dashboards.dashboard.new',
		noTypeCode: 'dashboards.dashboards',
		createTypeCode: 'dashboards.dashboard',
		sectionCode: 'dashboards.dashboards',
		sectionIcon: 'dashboard',
		singularCode: 'dashboards.dashboard',
		pluralCode: 'dashboards.dashboards',
		requiresOrg: true
	},

	datasets: {
		section: 'datasets',
		domainType: 'dataset',
		addLabelCode:  isV6() ? 'datasets.dataset' : 'datasets.new',
		noTypeText: 'datasets.missing.domainObject.1',
		createTypeCode: 'datasets.dataset',
		sectionCode: 'datasets.datasets',
		sectionIcon: 'dataset',
		singularCode: 'datasets.dataset',
		pluralCode:'datasets.datasets',
		itemCode: 'datasets.dataset',
		requiresOrg:  true
	},

	initiatives: {
		section: 'initiatives',
		domainType: 'initiative',
		addLabelCode:  isV6() ? 'initiatives.initiative' : 'initiatives.newInitiative',
		noTypeCode: 'initiatives.initiatives',
		createTypeCode: 'initiatives.initiative',
		sectionCode: 'initiatives.initiatives',
		sectionIcon: 'initiatives',
		singularCode: 'initiatives.initiative',
		pluralCode: 'initiatives.initiatives',
		itemCode: 'initiatives.initiativeItem',
		requiresOrg: true
	},

	briefings: {
		section: 'briefings',
		domainType: 'briefing',
		addLabelCode:  isV6() ? 'briefingBooks.briefing' : 'briefingBooks.briefings.new',
		noTypeCode: 'briefingBooks.briefings',
		createTypeCode: 'briefingBooks.briefing',
		sectionCode: 'briefingBooks.briefings',
		sectionIcon: 'briefings',
		slides: {
			section: 'briefings',
			sectionCode: 'briefingBooks.briefings.slides',
			pluralCode: 'briefingBooks.briefings.slides'
		},
		singularCode: 'briefingBooks.briefing',
		pluralCode: 'briefingBooks.briefings',
		requiresOrg: true
	},

	// #21214 change "documents" section to "files"
	files: {
		section: 'files',
		domainType: 'file',
		addLabelCode:  isV6() ? 'documents.document' : 'documents.upload.document',
		noTypeCode: 'documents.documents',
		createTypeCode: 'documents.document',
		sectionCode: 'documents.documents',
		sectionIcon: 'docs',
		singularCode: 'documents.document',
		pluralCode: 'documents.documents',
		requiresOrg: true
	},

	// I only added the options that are actually in use so it's clear that
	// changing these will have consequences.  Adding something won't break
	// anything new, so adding is always safe.  You just have to be careful when
	// changing something
	images: {
		noTypeCode: 'admin.images.images',
		domainType: 'file',
		requiresOrg: true
	},

	'import-connection': {
		pluralCode: 'admin.importConnections'
	},

	reports: {
		section: 'reports',
		domainType: 'report',
		addLabelCode: isV6() ? 'report.report' : 'report.common.new',
		noTypeCode: 'report.reports',
		createTypeCode: 'report.report',
		sectionCode: 'report.reports',
		sectionIcon: 'reports',
		singularCode: 'report.report',
		pluralCode: 'report.reports',
		requiresOrg: true
	},

	bookmarks: {
		section: 'bookmarks',
		domainType: 'bookmark',
		addLabelCode: isV6() ? 'bookmarks.bookmark' : 'bookmarks.bookmark.new',
		noTypeCode: 'bookmarks.bookmarks',
		createTypeCode: 'bookmarks.bookmark',
		sectionCode: 'bookmarks.bookmarks',
		sectionIcon: 'bookmark',
		singularCode: 'bookmarks.bookmark',
		pluralCode:'bookmarks.bookmarks'
	},

	home: {
		section: 'home',
		sectionCode: 'home.home',
		sectionIcon: 'group'
	},

	admin: {
		section: 'admin',
		sectionCode: 'admin.admin'
	},

	'import': {
		section: 'imports'
	},

	calendars: {
		section: 'calendars',
		sectionCode: 'common.calendar.calendars',
		sectionIcon: 'calendar',
		singularCode: 'common.calendar.calendar'
	},

	scoringTypes: {
		singularCode: 'report.customReport.scoringType',
		pluralCode: 'admin.appConfiguration.scoringTypes'
	},

	'dataset-tree': {
		singularCode: 'datasets.dataType.rollupTree',
		pluralCode: 'datasets.dataType.rollupTrees'
	},

	forms: {
		section: 'forms',
		domainType: 'form',
		addLabelCode:  isV6() ? 'forms.form' : 'forms.new',
		noTypeCode: 'forms.forms',
		createTypeCode: 'forms.form',
		sectionCode: 'forms.forms',
		sectionIcon: 'form',
		singularCode: 'forms.form',
		pluralCode: 'forms.forms',
		requiresOrg: true
	},

	users: {
		domainType: 'user',
		sectionCode: 'common.user.users',
		sectionIcon: 'user-avatar',
		singularCode: 'common.user.user',
		pluralCode: 'common.user.users'
	},

	'user-permissions-audit': {
		section: 'user-permissions-audit',
		singularCode: 'admin.userPermissionsAudit'
	},

	'dataset-permissions-audit': {
		section: 'dataset-permissions-audit',
		singularCode: 'admin.datasetPermissionsAudit'
	},

	'org-permissions-audit': {
		section: 'org-permissions-audit',
		singularCode: 'admin.orgPermissionsAudit'
	}

}

metadataAliases(metadata)

/**
 * Recursively freezes the metadata object so that it can't be modified.
 * Unfortunately any modification attempt simply fails silently rather than
 * throwing an error, but I couldn't get an error to be thrown in a way that
 * worked with the rest of our codebase. I tried "use strict"
 * but that prevented the __filename__ from being added correctly. I also
 * tried some other variations on Object.create that I couldn't get working.
 */
function deepFreeze(obj) {
	Object.keys(obj).forEach(key => {
		var value = obj[key]
		if (typeof value === 'object' && value !== null) {
			deepFreeze(value)
		}
	})

	// Prevent deleting properties and throw an error
	Object.freeze(obj)
}

deepFreeze(metadata)

export default metadata

metadata.__filename__ = 'components/metadata.js'
